import React, { useState } from "react";

import getDispensariesV3 from "@/api/requests/consumerApi/getDispensariesV3";
import getShoppableDispensariesNearby from "@/api/requests/consumerApi/getShoppableDispensariesNearby";
import { DispensaryV3 } from "@/custom-types/DispensariesV3";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import useOnComponentInView from "@/hooks/useOnComponentInView";
import { useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import { getUserId } from "@/redux/selectors/user";
import { convertDispensaryToV3 } from "@/utils/dispensaryUtils";
import formatImgixUrl from "@/utils/image/formatImgixUrl";

import DispensariesCarouselView from "@/components/DispensariesCarouselView";

const DEFAULT_TAKE = 7;

type Props = {
  title: string;
  linkText: string;
  page: string;
  changeLocation?: boolean;
  link: string;
  trackCardImpression: (
    slot: number,
    dispensary: DispensaryV3,
    carouselType: string,
  ) => void;
};

const DispensariesCarouselContainer = ({
  title,
  linkText,
  changeLocation,
  link,
  trackCardImpression,
  page,
}: Props) => {
  const [dispensaries, setDispensaries] = useState<
    DispensaryV3[] | Record<string, never>[]
  >([{}, {}, {}, {}]);

  const coordinates = useSelector(getCoordinates);
  const countryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);
  const { publishEvent } = useEventTracker();

  const getData = async () => {
    publishEvent({
      action: "Impression",
      category: "Dispensaries Carousel",
      label: "Dispensaries Carousel",
      nonInteractionEvent: true,
    });

    const flags: Array<string> = [countryCode, "dispensary"];
    const { stores, sponsoredStores } = await getDispensariesV3({
      flags,
      lat: coordinates.lat,
      lon: coordinates.lon,
      radius: "20mi",
      take: DEFAULT_TAKE,
    });

    sponsoredStores.forEach((s) => (s.isAd = true));

    const dispensariesArray = [...sponsoredStores, ...stores]
      .slice(0, DEFAULT_TAKE)
      .map((d) => ({
        ...d,
        logoUrl: d.logoUrl ? formatImgixUrl(d.logoUrl) : d.logoUrl,
      }));

    if (dispensariesArray.length < DEFAULT_TAKE) {
      const backfillData = await getShoppableDispensariesNearby(
        coordinates,
        DEFAULT_TAKE - dispensariesArray.length,
      );
      const converted = convertDispensaryToV3(backfillData);
      dispensariesArray.push(...converted);
    }

    const uniqueDispensaries = dispensariesArray.filter(
      (dispensary, dispensaryIndex) =>
        dispensaryIndex ===
        dispensariesArray.findIndex(
          (innerDispensary) => innerDispensary.id === dispensary.id,
        ),
    );

    setDispensaries(uniqueDispensaries);
  };

  const [ref] = useOnComponentInView(getData, { freezeOnceVisible: true });

  if (dispensaries.length === 0 && !dispensaries.some((d) => d.name)) {
    return null;
  }

  return (
    <div ref={ref}>
      <DispensariesCarouselView
        dispensaries={dispensaries}
        countryCode={countryCode}
        title={title}
        linkText={linkText}
        link={link}
        userId={userId}
        changeLocation={changeLocation}
        trackCardImpression={trackCardImpression}
        page={page}
      />
    </div>
  );
};
export default DispensariesCarouselContainer;
